// ** React Imports
import { useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";

// ** Redux & Store & Actions
import { useSelector, useDispatch } from "react-redux";
import { getGallery } from "store/modules/main-pages/galleries/get-gallery.action";
import { closeFilters } from "store/modules/shared-filters.slice";

// ** Third Party Components
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";

// ** Custom Components
import { CardWrapper } from "components/layout/Card/CardWrapper";
import { CardTitle } from "components/layout/Card/CardTitle";
import { CardBody } from "components/layout/Card/CardBody";
import { CardsBlockWrapper } from "components/layout/Card/CardsBlockWrapper";
import Heading from "components/typography/Heading";
import ScrollToTop from "components/global/ScrollToTop";
import EmptyBlock from "components/layout/Block/BlockEmpty";

// ** Gallery Components
import { GalleryFiltersSection } from "./components/GalleryFiltersSection/GalleryFiltersSection";
import GalleryList from "./components/GalleryList/GalleryList";
import GalleryMasonry from "./components/GalleryMasonry";
import GalleryNavPanel from "./components/GalleryNav/GalleryNavPanel";

// ** Custom Hooks
import useGalleryMetaTags from "./hooks/useGalleryMetaTags";
import useSharedGalleryInfinityScroll from "hooks/useSharedGalleryInfinityScroll";
import { useSharedGalleryViewType } from "hooks/useSharedGalleryViewType";

// #####################################################

const Gallery = () => {
	const { t } = useTranslation(["pages"]);

	const language = useSelector((state) => state.globalCommon.language);
	const dispatch = useDispatch();

	const { pathname } = useLocation();

	const galleriesKey = useMemo(() => {
		if (pathname === "/galleries/photos") {
			return "galleriesPhotos";
		} else if (pathname === "/galleries/videos") {
			return "galleriesVideos";
		} else {
			return "galleriesAll";
		}
	}, [pathname]);

	const galleries = useSelector((state) => state[galleriesKey]);

	const containerRef = useRef(null);

	const { viewType, handleViewType } = useSharedGalleryViewType();

	const { triggerRef } = useSharedGalleryInfinityScroll({
		containerRef,
		galleriesKey,
		getGallery,
	});

	const { title, metaDescription, ogMetaTags, canonicalLink } =
		useGalleryMetaTags();

	const emptyText = useMemo(() => {
		if (pathname === "/galleries/photos") {
			return t("common:empty.photosList");
		} else if (pathname === "/galleries/videos") {
			return t("common:empty.videosList");
		} else {
			return t("common:empty.mediaList");
		}
	}, [pathname, language]);

	useEffect(() => {
		// ** TODO: Przywrócić zamykania filtrów
		// dispatch(closeFilters());
		if (!(galleries.loaded || galleries.loading)) {
			dispatch(getGallery({ siteName: galleriesKey }));
		}
	}, [galleries?.loaded, galleriesKey, galleries?.loading]);

	// #####################################################

	return (
		<div ref={containerRef}>
			{/* Sekcja HEAD */}
			<Helmet title={title}>
				<meta name="description" content={metaDescription} />
				{ogMetaTags}
				{canonicalLink()}
			</Helmet>

			<ScrollToTop />
			<CardsBlockWrapper>
				<CardWrapper>
					<CardTitle>
						<Heading type="h2" data-testid="gallery-heading">
							{t("pages:galleries")}
						</Heading>
					</CardTitle>

					<CardBody>
						<GalleryFiltersSection galleriesKey={galleriesKey} />

						<GalleryNavPanel
							viewType={viewType}
							handleViewType={handleViewType}
							galleriesKey={galleriesKey}
						/>
					</CardBody>
				</CardWrapper>
				{viewType === "list" ? (
					<GalleryList
						emptyText={emptyText}
						triggerRef={triggerRef}
						galleriesKey={galleriesKey}
					/>
				) : (
					<CardWrapper>
						<CardBody $customPaddingInMobile={"0.75rem 3px"}>
							<GalleryMasonry
								emptyText={emptyText}
								triggerRef={triggerRef}
								galleriesKey={galleriesKey}
							/>
						</CardBody>
					</CardWrapper>
				)}
				<EmptyBlock />
			</CardsBlockWrapper>
		</div>
	);
};

// #####################################################

export default Gallery;

// #####################################################
